/**
 * canvas-engine/model/canvasModel.js
 *
 * OWN, self-contained canvas model primitives for the Enterprise Canvas Builder.
 *
 * ZERO-REUSE CONSTRAINT: this module imports NOTHING from
 * `admin-console-ui/src/modules/template-builder/` or `academy-ui/src/template/`.
 * It MAY import its own siblings `./schema` and `./assetRef` only.
 *
 * Pure module — no React, no DOM, no side effects (other than internal id
 * generation). Every exported function returns NEW values and never mutates
 * its inputs, so it is safe to share between the in-process Console Preview
 * (builder mode) and the published Reader (reader mode) — parity by construction.
 *
 * Public surface (design: Components and Interfaces — model/canvasModel):
 *   - CANVAS_SCHEMA_VERSION (re-exported from schema)
 *   - ELEMENT_DEFAULTS
 *   - defaultElement(type)
 *   - ensureElementDefaults(el)
 *   - normalizeCanvas(c)
 *   - validateCanvas(c)
 *   - isCanvasModel(c)
 *   - applyCanvasPatch(canvas, patch)   ← the single edit contract
 *
 * Requirements: 19.1 (persist edits into content.canvas), 22.3 (single color
 * representation — CSS color strings on element.style[...]), 22.4 (single asset
 * representation — { storedLink, displayLink }), 24.1 (defined fallback for
 * invalid canvas data), 24.2 (defined defaults for missing element fields).
 */

import {
  CANVAS_SCHEMA_VERSION,
  REFERENCE_WIDTH,
  DEFAULT_FRAME_HEIGHT,
  DEFAULT_FULL_BLEED,
  DEFAULT_CONTENT_INSET,
  DEFAULT_CONTENT_MAX_WIDTH,
  LAYOUT_MODE_ABSOLUTE,
  ELEMENT_TYPES,
  MIN_DIMENSION,
  MIN_OPACITY,
  MAX_OPACITY,
} from './schema';
import { normalizeAsset } from './assetRef';

// Re-export the schema version so consumers can import everything model-related
// from this one module. (Req 19.1)
export { CANVAS_SCHEMA_VERSION };

// ── Small pure helpers ────────────────────────────────────────────────────

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep clone of any JSON-shaped value (arrays/objects/primitives). */
function deepClone(v) {
  if (Array.isArray(v)) return v.map(deepClone);
  if (isPlainObject(v)) {
    const out = {};
    const keys = Object.keys(v);
    for (let i = 0; i < keys.length; i += 1) {
      out[keys[i]] = deepClone(v[keys[i]]);
    }
    return out;
  }
  return v;
}

/** Return value when finite, else fallback. */
function finiteOr(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

/** Return a finite dimension clamped to >= MIN_DIMENSION, else fallback. */
function dimensionOr(value, fallback) {
  if (!Number.isFinite(value)) return fallback;
  return value < MIN_DIMENSION ? MIN_DIMENSION : value;
}

/** Return a finite, non-negative number (>= 0), else fallback. */
function nonNegativeOr(value, fallback) {
  if (!Number.isFinite(value) || value < 0) return fallback;
  return value;
}

/** Clamp an opacity-like value into the inclusive [MIN_OPACITY, MAX_OPACITY] range. */
function clampOpacity(value) {
  if (!Number.isFinite(value)) return MAX_OPACITY;
  if (value < MIN_OPACITY) return MIN_OPACITY;
  if (value > MAX_OPACITY) return MAX_OPACITY;
  return value;
}

/** Clone a value only when it is a plain object, otherwise return {}. */
function cloneObjectOr(v) {
  return isPlainObject(v) ? deepClone(v) : {};
}

// Internal id counter — gives each generated element a unique-within-session id.
let ID_COUNTER = 0;

/** Generate a unique element id token for a given type. */
function generateElementId(type) {
  ID_COUNTER += 1;
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  const seq = ID_COUNTER.toString(36);
  return `${type}-${time}-${seq}${rand}`;
}

/**
 * Content keys that carry a single asset reference `{ storedLink, displayLink }`
 * (Req 22.4). A `null` patch value on one of these keys clears it to the empty
 * asset; any non-null value is normalized through the own `normalizeAsset` so
 * there is never an alternate asset representation. (design: Single image /
 * asset reference)
 */
export const ASSET_CONTENT_KEYS = Object.freeze(['asset', 'image']);
const ASSET_KEY_SET = new Set(ASSET_CONTENT_KEYS);

/** Frame keys that REPLACE (never deep-merge) when present in a patch. */
const FRAME_KEYS = Object.freeze(['x', 'y', 'width', 'height', 'zIndex']);

// ── Optional per-element customization contracts ────────────────────────────
//
// The fields below are ALL OPTIONAL and additive. An element that omits them
// behaves exactly as before — `defaultElement` never seeds them, and the
// normalizers only emit them when the caller supplied a value. They are shared,
// reused shapes (Link_Config / Animation_Config) used across header nav items,
// footer links, buttons, link-converted elements, and animatable targets.
// (design: Per-element background and convert-to-link; Data Models)

/** Allowed Link_Config `type` tokens. */
const LINK_TYPES = Object.freeze(['internal', 'external', 'document']);
/** Allowed Link_Config `target` tokens. */
const LINK_TARGETS = Object.freeze(['_self', '_blank']);
/** Allowed Link_Config `documentAction` tokens (only meaningful for `document`). */
const LINK_DOCUMENT_ACTIONS = Object.freeze(['open', 'download']);

/** Allowed Animation_Config `type` tokens. */
const ANIMATION_TYPES = Object.freeze([
  'none',
  'fade',
  'slide-in',
  'slide-up',
  'slide-down',
  'slide-left',
  'slide-right',
  'zoom',
]);
/** Allowed Animation_Config `trigger` tokens. */
const ANIMATION_TRIGGERS = Object.freeze(['load', 'scroll']);

/** Animation_Config defaults (design: Shared building blocks — Animation_Config). */
export const ANIMATION_CONFIG_DEFAULTS = Object.freeze({
  type: 'none',
  trigger: 'load',
  duration: 600,
  delay: 0,
  easing: 'ease',
});

/**
 * Normalize an optional Link_Config (+ `enabled` convert-to-link flag).
 *
 * Returns a NEW normalized link config when `value` is a plain object, or
 * `undefined` when absent/invalid so callers can leave the field off entirely
 * (preserving current behavior). Known sub-fields are coerced to their allowed
 * tokens with the documented defaults; unknown caller keys are dropped so the
 * shape stays the single shared contract. (Req 4.4)
 *
 * @param {*} value a (possibly partial/invalid) link config
 * @returns {object|undefined} `{ type, url, target, documentAction?, enabled }`
 */
export function normalizeLinkConfig(value) {
  if (!isPlainObject(value)) return undefined;

  const type = LINK_TYPES.includes(value.type) ? value.type : 'internal';
  const out = {
    type,
    url: typeof value.url === 'string' ? value.url : '',
    target: LINK_TARGETS.includes(value.target) ? value.target : '_self',
    enabled: value.enabled === true,
  };

  if (type === 'document' && LINK_DOCUMENT_ACTIONS.includes(value.documentAction)) {
    out.documentAction = value.documentAction;
  }

  return out;
}

/**
 * Normalize an optional Animation_Config.
 *
 * Returns a NEW normalized config when `value` is a plain object, or
 * `undefined` when absent/invalid so callers can leave `element.animation` off
 * entirely (no animation — current behavior). Each field is coerced to its
 * allowed token / a finite non-negative number, falling back to the
 * Animation_Config defaults. (Req 4.4 — additive optional field)
 *
 * @param {*} value a (possibly partial/invalid) animation config
 * @returns {object|undefined} `{ type, trigger, duration, delay, easing }`
 */
export function normalizeAnimationConfig(value) {
  if (!isPlainObject(value)) return undefined;

  return {
    type: ANIMATION_TYPES.includes(value.type) ? value.type : ANIMATION_CONFIG_DEFAULTS.type,
    trigger: ANIMATION_TRIGGERS.includes(value.trigger)
      ? value.trigger
      : ANIMATION_CONFIG_DEFAULTS.trigger,
    duration: nonNegativeOr(value.duration, ANIMATION_CONFIG_DEFAULTS.duration),
    delay: nonNegativeOr(value.delay, ANIMATION_CONFIG_DEFAULTS.delay),
    easing: typeof value.easing === 'string' ? value.easing : ANIMATION_CONFIG_DEFAULTS.easing,
  };
}

/**
 * Normalize the optional per-element customization fields IN PLACE on a freshly
 * built element. Pure with respect to the caller's input (operates on the
 * already-cloned `out`/`out.style`/`out.content`). Every field is left absent
 * when the source did not provide it, so an element that carried none of these
 * fields is unchanged. (design: Data Models — Element)
 *
 *   - style.backgroundTransparent : coerced to boolean when present
 *   - style.backgroundColor       : kept as a string when present
 *   - content.link                : normalized Link_Config (+ enabled) when present
 *   - animation                   : normalized Animation_Config when present
 *
 * @param {object} out a complete element being assembled by ensureElementDefaults
 * @param {object} src the original (possibly partial) element input
 */
function applyOptionalElementFields(out, src) {
  // style.backgroundTransparent / style.backgroundColor ride the style spread;
  // coerce/drop them so only well-formed optional values survive.
  if ('backgroundTransparent' in out.style) {
    out.style.backgroundTransparent = out.style.backgroundTransparent === true;
  }
  if ('backgroundColor' in out.style && typeof out.style.backgroundColor !== 'string') {
    delete out.style.backgroundColor;
  }

  // content.link — convert-to-link config. Normalize when present, else drop.
  if ('link' in out.content) {
    const link = normalizeLinkConfig(out.content.link);
    if (link) {
      out.content.link = link;
    } else {
      delete out.content.link;
    }
  }

  // animation — top-level optional Animation_Config; emitted only when supplied.
  if (isPlainObject(src) && 'animation' in src) {
    const animation = normalizeAnimationConfig(src.animation);
    if (animation) out.animation = animation;
  }
}

// ── Frame defaults ─────────────────────────────────────────────────────────

/**
 * Default Canvas_Model frame envelope (without elements). Width is the fixed
 * Reference_Width for v1. (Req 21.1, 24.1)
 *
 * The optional frame-layout metadata fields `fullBleed`, `contentInset`, and
 * `contentMaxWidth` are additive and backward compatible: older canvases that
 * omit them remain valid (see `validateCanvas`) and `normalizeCanvas` fills the
 * defaults below. They are pure layout metadata consumed by the shared stacking
 * renderer and never affect element geometry. (Req 4.3, 4.4, 5.1, 7.4)
 */
export const FRAME_DEFAULTS = Object.freeze({
  width: REFERENCE_WIDTH,
  height: DEFAULT_FRAME_HEIGHT,
  background: 'linear-gradient(135deg,#07111f,#1d4ed8)',
  radius: 28,
  shadow: '0 30px 80px rgba(15,23,42,.18)',
  padding: '72px 24px',
  responsive: { mode: 'fixed' },
  fullBleed: DEFAULT_FULL_BLEED,
  contentInset: DEFAULT_CONTENT_INSET,
  contentMaxWidth: DEFAULT_CONTENT_MAX_WIDTH,
});

// ── Per-type element defaults ───────────────────────────────────────────────

/**
 * Default frame size (width/height in px at the Reference_Width) for a freshly
 * created element of each type. Kept separate from ELEMENT_DEFAULTS so the
 * latter stays a pure content/style/layout/binding description.
 */
const ELEMENT_SIZE_DEFAULTS = Object.freeze({
  text: { width: 560, height: 220 },
  image: { width: 480, height: 320 },
  button: { width: 220, height: 56 },
  card: { width: 360, height: 360 },
  grid: { width: 1040, height: 360 },
  imageGrid: { width: 1040, height: 360 },
  html: { width: 1040, height: 240 },
  logo: { width: 200, height: 64 },
  icon: { width: 64, height: 64 },
  nav: { width: 720, height: 64 },
  social: { width: 280, height: 56 },
  dataBound: { width: 1040, height: 420 },
});

/**
 * Per-type `content` / `style` (and where relevant `layout` / `binding`)
 * defaults for every supported Canvas_Element type. Colors are single CSS
 * color strings (Req 22.3); image content keys hold an empty asset object that
 * the asset-key normalization pass converts via `normalizeAsset` (Req 22.4).
 *
 * Every key required by the renderers and inspector control sets (Req 13–17)
 * is present so `ensureElementDefaults` can fill any missing value while
 * preserving caller-supplied ones. (Req 24.2)
 */
export const ELEMENT_DEFAULTS = Object.freeze({
  text: {
    content: { eyebrow: '', heading: 'Heading', body: 'Body text' },
    style: {
      headingSize: 32,
      headingColor: '#0f172a',
      bodyColor: '#334155',
      eyebrowColor: '#2563eb',
      fontWeight: 600,
      textAlign: 'left',
    },
  },
  image: {
    content: { asset: { storedLink: '', displayLink: '' }, src: '', alt: '' },
    style: {
      objectFit: 'cover',
      objectPosition: 'center',
      radius: 16,
      shadow: 'none',
      opacity: 1,
    },
  },
  button: {
    content: { label: 'Button', url: '', linkType: 'url', newTab: false },
    style: {
      background: '#2563eb',
      color: '#ffffff',
      borderColor: 'transparent',
      radius: 12,
      fontSize: 16,
      fontWeight: 600,
      letterSpacing: 0,
    },
  },
  card: {
    content: { title: 'Card title', description: 'Card description', image: { storedLink: '', displayLink: '' } },
    style: {
      background: '#ffffff',
      borderColor: '#e2e8f0',
      radius: 18,
      shadow: '0 12px 32px rgba(15,23,42,.10)',
      padding: '24px',
      titleColor: '#0f172a',
      descriptionColor: '#475569',
    },
  },
  grid: {
    content: { items: [] },
    style: { imageRadius: 12, objectFit: 'cover' },
    layout: { columns: 3, gap: 14 },
  },
  imageGrid: {
    content: { images: [] },
    style: { imageRadius: 12, objectFit: 'cover' },
    layout: { columns: 3, gap: 14 },
  },
  html: {
    content: { html: '', css: '' },
    style: {},
  },
  logo: {
    content: { asset: { storedLink: '', displayLink: '' }, alt: 'Logo' },
    style: { background: 'transparent', radius: 0, color: '#0f172a' },
  },
  icon: {
    content: { asset: { storedLink: '', displayLink: '' }, alt: 'Icon' },
    style: { background: 'transparent', radius: 0, color: '#0f172a' },
  },
  nav: {
    content: { items: [] },
    style: { background: 'transparent', color: '#0f172a', fontSize: 16, fontWeight: 500 },
  },
  social: {
    content: { items: [] },
    style: { background: 'transparent', color: '#0f172a', iconSize: 20 },
  },
  dataBound: {
    content: { title: '', subtitle: '' },
    style: { background: 'transparent', color: '#0f172a' },
    binding: { source: '', params: {} },
  },
});

// ── Element primitives ───────────────────────────────────────────────────

/**
 * Normalize the asset-bearing content keys of an element content object into
 * the single `{ storedLink, displayLink }` representation. (Req 22.4)
 * Returns the same object reference (mutates the passed-in fresh content).
 */
function normalizeAssetContentKeys(content) {
  for (let i = 0; i < ASSET_CONTENT_KEYS.length; i += 1) {
    const key = ASSET_CONTENT_KEYS[i];
    if (key in content) {
      content[key] = normalizeAsset(content[key]);
    }
  }
  return content;
}

/**
 * Resolve a type token to a supported element type, defaulting unknown/missing
 * types to `text`.
 */
function resolveType(type) {
  return ELEMENT_TYPES.includes(type) ? type : 'text';
}

/**
 * Create a brand-new element of the given type with a fresh unique id, default
 * positional fields, and the per-type content/style (and layout/binding)
 * defaults. Pure aside from id generation. (design: defaultElement)
 *
 * @param {string} type one of ELEMENT_TYPES (unknown → 'text')
 * @returns {object} a new Canvas_Element
 */
export function defaultElement(type) {
  const t = resolveType(type);
  const defaults = ELEMENT_DEFAULTS[t];
  const size = ELEMENT_SIZE_DEFAULTS[t] || { width: 320, height: 120 };

  const el = {
    id: generateElementId(t),
    type: t,
    x: 0,
    y: 0,
    width: size.width,
    height: size.height,
    zIndex: 0,
    rotation: 0,
    locked: false,
    hidden: false,
    content: normalizeAssetContentKeys(deepClone(defaults.content)),
    style: deepClone(defaults.style),
  };

  if (defaults.layout) el.layout = deepClone(defaults.layout);
  if (defaults.binding) el.binding = deepClone(defaults.binding);

  return el;
}

/**
 * Return a NEW element with all required fields present. Missing positional
 * fields are filled with finite defaults (width/height clamped to >=
 * MIN_DIMENSION); the type defaults to a supported type; opacity is clamped to
 * [0,1]; required per-type content/style/layout/binding keys are filled with
 * defaults while every caller-supplied value is preserved; asset content keys
 * are normalized to `{ storedLink, displayLink }`. Never mutates the input.
 * (Req 24.2, 22.4)
 *
 * @param {object} el a (possibly partial/invalid) element
 * @returns {object} a complete element
 */
export function ensureElementDefaults(el) {
  const src = isPlainObject(el) ? el : {};
  const type = resolveType(src.type);
  const defaults = ELEMENT_DEFAULTS[type];
  const size = ELEMENT_SIZE_DEFAULTS[type] || { width: 320, height: 120 };

  const content = { ...deepClone(defaults.content), ...cloneObjectOr(src.content) };
  normalizeAssetContentKeys(content);

  const style = { ...deepClone(defaults.style), ...cloneObjectOr(src.style) };
  if ('opacity' in style) {
    style.opacity = clampOpacity(style.opacity);
  }

  const out = {
    id: typeof src.id === 'string' && src.id.length > 0 ? src.id : generateElementId(type),
    type,
    x: finiteOr(src.x, 0),
    y: finiteOr(src.y, 0),
    width: dimensionOr(src.width, size.width),
    height: dimensionOr(src.height, size.height),
    zIndex: finiteOr(src.zIndex, 0),
    rotation: finiteOr(src.rotation, 0),
    locked: typeof src.locked === 'boolean' ? src.locked : false,
    hidden: typeof src.hidden === 'boolean' ? src.hidden : false,
    content,
    style,
  };

  if (defaults.layout) {
    out.layout = { ...deepClone(defaults.layout), ...cloneObjectOr(src.layout) };
  }
  if (defaults.binding) {
    out.binding = { ...deepClone(defaults.binding), ...cloneObjectOr(src.binding) };
  }

  // Preserve/normalize the optional per-element customization fields (element
  // background, convert-to-link, animation). Absent ⇒ unchanged. (Req 4.1, 4.2, 4.4)
  applyOptionalElementFields(out, src);

  return out;
}

// ── Canvas model normalization / validation ────────────────────────────────

/**
 * Return a complete, valid Canvas_Model from any (partial/invalid) input: a
 * fully-defaulted frame plus an ordered `elements[]` where each element is
 * passed through `ensureElementDefaults`. Always emits `layoutMode: "absolute"`
 * and tolerates missing/non-array elements (→ []). Preserves every supplied
 * value. Pure — never mutates the input. (Req 24.1, 24.2)
 *
 * @param {*} c a (possibly partial/invalid) canvas model
 * @returns {object} a complete, valid Canvas_Model
 */
export function normalizeCanvas(c) {
  const src = isPlainObject(c) ? c : {};
  const elements = Array.isArray(src.elements) ? src.elements.map(ensureElementDefaults) : [];

  const responsive =
    isPlainObject(src.responsive) && typeof src.responsive.mode === 'string'
      ? { ...deepClone(src.responsive) }
      : { ...FRAME_DEFAULTS.responsive };

  const model = {
    schemaVersion: CANVAS_SCHEMA_VERSION,
    width: dimensionOr(src.width, FRAME_DEFAULTS.width),
    height: dimensionOr(src.height, FRAME_DEFAULTS.height),
    background: typeof src.background === 'string' ? src.background : FRAME_DEFAULTS.background,
    radius: finiteOr(src.radius, FRAME_DEFAULTS.radius),
    shadow: typeof src.shadow === 'string' ? src.shadow : FRAME_DEFAULTS.shadow,
    padding: typeof src.padding === 'string' ? src.padding : FRAME_DEFAULTS.padding,
    responsive,
    layoutMode: LAYOUT_MODE_ABSOLUTE,
    // Optional frame-layout metadata (additive, backward compatible). Copied
    // through when present and valid, else defaulted. Pure layout metadata for
    // the stacking renderer — never affects element geometry. (Req 4.3, 4.4, 5.1)
    fullBleed: typeof src.fullBleed === 'boolean' ? src.fullBleed : FRAME_DEFAULTS.fullBleed,
    contentInset: nonNegativeOr(src.contentInset, FRAME_DEFAULTS.contentInset),
    contentMaxWidth: dimensionOr(src.contentMaxWidth, FRAME_DEFAULTS.contentMaxWidth),
    elements,
  };

  // Preserve optional migration provenance if supplied.
  if (isPlainObject(src.source)) {
    model.source = deepClone(src.source);
  }
  // Preserve an optional frame backgroundImage asset if supplied.
  if ('backgroundImage' in src) {
    model.backgroundImage = normalizeAsset(src.backgroundImage);
  }

  return model;
}

/**
 * Boolean structural validity check: true when `c` is a normalized, renderable
 * Canvas_Model — has a numeric schemaVersion, finite frame dims (>= 1), an
 * elements array, and every element has a known type and finite positional
 * fields. (Req 24.1)
 *
 * @param {*} c
 * @returns {boolean}
 */
export function validateCanvas(c) {
  if (!isPlainObject(c)) return false;
  if (typeof c.schemaVersion !== 'number') return false;
  if (!Number.isFinite(c.width) || c.width < MIN_DIMENSION) return false;
  if (!Number.isFinite(c.height) || c.height < MIN_DIMENSION) return false;
  if (!Array.isArray(c.elements)) return false;
  // NOTE: the optional frame-layout fields (fullBleed/contentInset/
  // contentMaxWidth) are intentionally NOT required here — canvases authored
  // before they existed remain valid. (Req 7.4 non-regression)

  for (let i = 0; i < c.elements.length; i += 1) {
    const el = c.elements[i];
    if (!isPlainObject(el)) return false;
    if (!ELEMENT_TYPES.includes(el.type)) return false;
    if (!Number.isFinite(el.x) || !Number.isFinite(el.y)) return false;
    if (!Number.isFinite(el.width) || el.width < MIN_DIMENSION) return false;
    if (!Number.isFinite(el.height) || el.height < MIN_DIMENSION) return false;
    if (!Number.isFinite(el.zIndex)) return false;
    // NOTE: the optional per-element customization fields
    // (style.backgroundTransparent / style.backgroundColor, content.link,
    // animation) are intentionally NOT required here — elements authored before
    // they existed remain valid, and any present values are normalized by
    // ensureElementDefaults. (Req 4.1, 4.2, 4.4 — additive, backward compatible)
  }

  return true;
}

/**
 * Cheap structural check used to detect already-canvas data (the shape stored
 * at `section.content.canvas`). Does NOT fully validate; just answers "does
 * this look like a Canvas_Model?". (design: isCanvasModel)
 *
 * @param {*} c
 * @returns {boolean}
 */
export function isCanvasModel(c) {
  return isPlainObject(c) && Array.isArray(c.elements) && typeof c.schemaVersion === 'number';
}

// ── The single edit contract ─────────────────────────────────────────────

/**
 * Deep-merge `patch` into `target` returning a NEW object. Plain objects merge
 * recursively; arrays and primitives replace. Inputs are never mutated.
 */
function deepMerge(target, patch) {
  const out = isPlainObject(target) ? { ...target } : {};
  const keys = Object.keys(patch);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const val = patch[key];
    if (isPlainObject(val) && isPlainObject(out[key])) {
      out[key] = deepMerge(out[key], val);
    } else if (isPlainObject(val) || Array.isArray(val)) {
      out[key] = deepClone(val);
    } else {
      out[key] = val;
    }
  }
  return out;
}

/**
 * Deep-merge a content patch, applying the single-asset-representation rule on
 * asset-bearing keys: a `null` value clears the key to an empty asset; any
 * other value is normalized through `normalizeAsset` and REPLACES the key.
 * (Req 22.4)
 */
function mergeContent(target, patch) {
  const out = isPlainObject(target) ? { ...target } : {};
  const keys = Object.keys(patch);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const val = patch[key];
    if (ASSET_KEY_SET.has(key)) {
      // null clears to empty asset; anything else is normalized & replaced.
      out[key] = val == null ? normalizeAsset(null) : normalizeAsset(val);
    } else if (isPlainObject(val) && isPlainObject(out[key])) {
      out[key] = deepMerge(out[key], val);
    } else if (isPlainObject(val) || Array.isArray(val)) {
      out[key] = deepClone(val);
    } else {
      out[key] = val;
    }
  }
  return out;
}

/**
 * The SINGLE mutation primitive. Apply an edit patch to one element of a canvas
 * model and return a NEW model. (Req 19.1, 22.3, 22.4)
 *
 * patch shape: `{ elementId, frame?, style?, content?, layout?, binding?, animation? }`
 *   - `frame` keys REPLACE the element's positional fields (x/y/width/height/zIndex).
 *   - `style`, `content`, `layout`, `binding` DEEP-MERGE.
 *   - `animation` is an optional top-level Animation_Config: a `null` value
 *     clears it (back to no animation), any other value is normalized and
 *     REPLACES the element's animation. (Req 4.4)
 *   - a `null` value on an asset-bearing `content` key clears it to an empty
 *     asset reference; any other asset value is normalized to
 *     `{ storedLink, displayLink }`.
 *
 * The optional per-element background (`style.backgroundTransparent` /
 * `style.backgroundColor`) and convert-to-link (`content.link`) fields ride the
 * existing `style` / `content` deep-merge paths, so no special handling is
 * needed for them here. (Req 4.1, 4.2)
 *
 * Only the targeted element changes; every other element and every unrelated
 * key is left exactly as supplied (no normalization side effects). The input is
 * never mutated. If `elementId` is not found, an equivalent new model is
 * returned unchanged.
 *
 * @param {object} canvas a Canvas_Model
 * @param {object} patch the edit patch
 * @returns {object} a new Canvas_Model
 */
export function applyCanvasPatch(canvas, patch) {
  // Always return a NEW model. Guard non-object canvas through normalization.
  if (!isPlainObject(canvas)) {
    return normalizeCanvas(canvas);
  }

  const next = deepClone(canvas);
  if (!Array.isArray(next.elements)) {
    next.elements = [];
  }

  if (!isPlainObject(patch) || patch.elementId == null) {
    return next; // nothing to apply → equivalent new model
  }

  const idx = next.elements.findIndex((el) => isPlainObject(el) && el.id === patch.elementId);
  if (idx === -1) {
    return next; // element not found → unchanged equivalent new model
  }

  const target = next.elements[idx];

  // Frame keys REPLACE.
  if (isPlainObject(patch.frame)) {
    for (let i = 0; i < FRAME_KEYS.length; i += 1) {
      const key = FRAME_KEYS[i];
      if (key in patch.frame) {
        target[key] = patch.frame[key];
      }
    }
  }

  // style / layout / binding DEEP-MERGE.
  if (isPlainObject(patch.style)) {
    target.style = deepMerge(target.style, patch.style);
  }
  if (isPlainObject(patch.layout)) {
    target.layout = deepMerge(target.layout, patch.layout);
  }
  if (isPlainObject(patch.binding)) {
    target.binding = deepMerge(target.binding, patch.binding);
  }

  // content DEEP-MERGE with single-asset-representation handling.
  if (isPlainObject(patch.content)) {
    target.content = mergeContent(target.content, patch.content);
  }

  // animation — optional top-level Animation_Config. `null` clears it; any
  // other value is normalized and replaces the element's animation. (Req 4.4)
  if ('animation' in patch) {
    if (patch.animation == null) {
      delete target.animation;
    } else {
      const animation = normalizeAnimationConfig(patch.animation);
      if (animation) target.animation = animation;
    }
  }

  return next;
}
